import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useInPhrase } from '../contexts/GeoCountryContext';
import Header from './Header';
import Footer from './Footer';
import { Building2, Package, Truck, Users, Award, HeadphonesIcon, CheckCircle2, ArrowRight, Mail, Tag, Sparkles, Shield, Globe } from 'lucide-react';

const DistributorPage = () => {
  const { currentLanguage, t: tg } = useLanguage();
  const inPhrase = useInPhrase(currentLanguage);

  const translations = {
    en: {
      title: 'Become Our Distributor',
      subtitle: 'Wholesale Peptides: Your Opportunity for Success',
      intro: "Hello, if you're here, you've probably heard of PenLab Peptide, a leading supplier of high-quality peptides {loc} and worldwide. We offer a wide range of peptides for scientific, medical, and cosmetic applications.",
      
      featuresTitle: 'Features of Our Products',
      features: [
        {
          title: 'Unique Format',
          description: 'Our peptides come in "peptide pen" format, combining convenience and efficacy for easy administration and precise dosing.'
        },
        {
          title: 'White Label Option',
          description: 'For clients and distributors purchasing in large quantities, we offer white label solutions, allowing easy brand recognition and strengthening your market position.'
        }
      ],
      
      wholesaleTitle: 'Wholesale Peptide Trade: Your Opportunity for Success',
      wholesaleIntro: 'We understand business dynamics and our partners\' needs. That\'s why PenLab Peptide offers special conditions for distributors who want to buy our products wholesale:',
      wholesalePoints: [
        'Attractive prices with significant discounts on large orders',
        'Fast and secure delivery at the right temperature anywhere in the world',
        'Specialized consultations and training on products and their application'
      ],
      
      whyTitle: 'Why Become Our Distributor?',
      whyReasons: [
        {
          title: 'High Quality',
          description: 'Our priority has always been product quality, which guarantees customer trust and satisfaction.'
        },
        {
          title: 'Training & Support',
          description: 'We offer training, resources, and constant support to all our distributors so they can successfully present and sell our products.'
        },
        {
          title: 'Loyal Conditions',
          description: 'We value our partners and offer competitive discounts and flexible terms.'
        }
      ],
      
      whiteLabelTitle: 'White Label Solutions',
      whiteLabelIntro: 'Build your own brand with our white label program:',
      whiteLabelPoints: [
        'Custom branding with your logo and design',
        'Professional packaging and labeling',
        'Complete product documentation',
        'Marketing support materials'
      ],
      whiteLabelMin: 'Minimum Order Requirement',
      whiteLabelMinDesc: '1,000 units per product per month',
      
      expectTitle: 'What We Expect From You',
      expectations: [
        'Desire to grow and learn',
        'Knowledge of the market and customers in your country or region',
        'Commitment to the high standards of service and professionalism that PenLab Peptide maintains'
      ],
      
      ctaTitle: 'Ready to Get Started?',
      ctaDesc: 'Contact us today to learn more about partnership opportunities or to begin the partnership process.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Contact Us',
      
      slogan: 'PenLab Peptide - Together we change the future of peptides!'
    },
    es: {
      title: 'Conviértete en Nuestro Distribuidor',
      subtitle: 'Péptidos al Por Mayor: Tu Oportunidad para el Éxito',
      intro: "Hola, si estás aquí, probablemente ya has oído hablar de PenLab Peptide, un líder proveedor de péptidos de alta calidad {loc} y a nivel mundial. Ofrecemos una amplia gama de péptidos para aplicaciones científicas, médicas y cosméticas.",
      
      featuresTitle: 'Características de Nuestros Productos',
      features: [
        {
          title: 'Formato Único',
          description: 'Nuestros péptidos se presentan en formato "pluma de péptido", que combina conveniencia y eficacia para una fácil administración y dosificación precisa.'
        },
        {
          title: 'Opción de Marca Blanca',
          description: 'Para clientes y distribuidores que compran en grandes cantidades, ofrecemos soluciones de marca blanca, lo que permite un fácil reconocimiento de marca y fortalece su posición en el mercado.'
        }
      ],
      
      wholesaleTitle: 'Comercio al Por Mayor de Péptidos: Tu Oportunidad para el Éxito',
      wholesaleIntro: 'Entendemos la dinámica empresarial y las necesidades de nuestros socios. Por eso, PenLab Peptide ofrece condiciones especiales para distribuidores que desean comprar nuestros productos al por mayor:',
      wholesalePoints: [
        'Precios atractivos con descuentos significativos en pedidos grandes',
        'Entrega rápida y segura a la temperatura adecuada a cualquier parte del mundo',
        'Consultas especializadas y formación sobre los productos y su aplicación'
      ],
      
      whyTitle: '¿Por Qué Convertirse en Nuestro Distribuidor?',
      whyReasons: [
        {
          title: 'Alta Calidad',
          description: 'Nuestra prioridad siempre ha sido la calidad de los productos, lo que garantiza la confianza y satisfacción de los clientes.'
        },
        {
          title: 'Formación y Apoyo',
          description: 'Ofrecemos formación, recursos y apoyo constante a todos nuestros distribuidores para que puedan presentar y vender nuestros productos con éxito.'
        },
        {
          title: 'Condiciones Leales',
          description: 'Valoramos a nuestros socios y ofrecemos descuentos competitivos y términos flexibles.'
        }
      ],
      
      whiteLabelTitle: 'Soluciones de Marca Blanca',
      whiteLabelIntro: 'Construye tu propia marca con nuestro programa de marca blanca:',
      whiteLabelPoints: [
        'Marca personalizada con tu logo y diseño',
        'Embalaje y etiquetado profesional',
        'Documentación completa del producto',
        'Materiales de apoyo de marketing'
      ],
      whiteLabelMin: 'Requisito de Pedido Mínimo',
      whiteLabelMinDesc: '1,000 unidades por producto al mes',
      
      expectTitle: '¿Qué Esperamos de Ti?',
      expectations: [
        'Deseo de crecer y aprender',
        'Conocimiento del mercado y de los clientes en tu país o región',
        'Compromiso con los altos estándares de servicio y profesionalismo que PenLab Peptide mantiene'
      ],
      
      ctaTitle: '¿Listo para Empezar?',
      ctaDesc: 'Contáctanos hoy para obtener más información sobre las oportunidades de asociación o para comenzar el proceso de asociación.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Contáctanos',
      
      slogan: '¡PenLab Peptide - Juntos cambiamos el futuro de los péptidos!'
    },
    ru: {
      title: 'Станьте нашим дистрибьютором',
      subtitle: 'Оптовые поставки пептидов: ваш путь к успеху',
      intro: "Добро пожаловать! Если вы здесь, то наверняка уже слышали о PenLab Peptide, ведущем поставщике высококачественных пептидов {loc} и по всему миру. Мы предлагаем широкий ассортимент пептидов для научных, медицинских и косметических исследований.",
      
      featuresTitle: 'Особенности нашей продукции',
      features: [
        {
          title: 'Уникальный формат',
          description: 'Наши пептиды поставляются в формате готовой шприц-ручки, сочетающей удобство и эффективность для простого применения и точного дозирования.'
        },
        {
          title: 'Решения под собственной маркой',
          description: 'Для клиентов и дистрибьюторов, закупающих большие объёмы, мы предлагаем решения white label, которые помогут укрепить узнаваемость вашего бренда на рынке.'
        }
      ],
      
      wholesaleTitle: 'Оптовые поставки пептидов: ваш путь к успеху',
      wholesaleIntro: 'Мы понимаем специфику бизнеса и потребности наших партнёров. Поэтому PenLab Peptide предлагает особые условия для дистрибьюторов, желающих приобретать продукцию оптом:',
      wholesalePoints: [
        'Привлекательные цены со значительными скидками на крупные заказы',
        'Быстрая и безопасная доставка с соблюдением температурного режима в любую точку мира',
        'Профессиональные консультации и обучение по продукции и её применению'
      ],
      
      whyTitle: 'Почему стоит стать нашим дистрибьютором?',
      whyReasons: [
        {
          title: 'Высокое качество',
          description: 'Качество продукции всегда было нашим приоритетом, что гарантирует доверие и удовлетворённость клиентов.'
        },
        {
          title: 'Обучение и поддержка',
          description: 'Мы обеспечиваем обучение, ресурсы и постоянную поддержку всем дистрибьюторам для успешного продвижения и продажи нашей продукции.'
        },
        {
          title: 'Выгодные условия',
          description: 'Мы ценим наших партнёров и предлагаем конкурентные скидки и гибкие условия сотрудничества.'
        }
      ],
      
      whiteLabelTitle: 'Решения под собственной маркой',
      whiteLabelIntro: 'Создайте собственный бренд с нашей программой white label:',
      whiteLabelPoints: [
        'Индивидуальный брендинг с вашим логотипом и дизайном',
        'Профессиональная упаковка и маркировка',
        'Полная документация на продукцию',
        'Маркетинговые материалы'
      ],
      whiteLabelMin: 'Минимальный объём заказа',
      whiteLabelMinDesc: '1 000 единиц на один продукт в месяц',
      
      expectTitle: 'Что мы ожидаем от вас',
      expectations: [
        'Стремление к развитию и обучению',
        'Знание рынка и клиентов в вашей стране или регионе',
        'Приверженность высоким стандартам обслуживания и профессионализма, которых придерживается PenLab Peptide'
      ],
      
      ctaTitle: 'Готовы начать?',
      ctaDesc: 'Свяжитесь с нами сегодня, чтобы узнать больше о возможностях партнёрства или начать процесс сотрудничества.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Связаться с нами',
      
      slogan: 'PenLab Peptide, вместе мы меняем будущее пептидов!'
    },
    de: {
      title: 'Werden Sie unser Vertriebspartner',
      subtitle: 'Großhandel mit Peptiden: Ihre Chance auf Erfolg',
      intro: 'Hallo, wenn Sie hier sind, haben Sie wahrscheinlich von PenLab Peptide gehört, einem führenden Anbieter von hochwertigen Peptiden {loc} und weltweit. Wir bieten eine breite Palette von Peptiden für wissenschaftliche, medizinische und kosmetische Anwendungen.',
      
      featuresTitle: 'Merkmale unserer Produkte',
      features: [
        {
          title: 'Einzigartiges Format',
          description: 'Unsere Peptide sind im „Peptid-Pen“-Format erhältlich und verbinden Anwenderfreundlichkeit und Wirksamkeit für eine einfache Applikation und präzise Dosierung.'
        },
        {
          title: 'White-Label-Option',
          description: 'Für Kunden und Vertriebspartner, die in großen Mengen einkaufen, bieten wir White-Label-Lösungen an, die eine einfache Markenwiedererkennung ermöglichen und Ihre Marktposition stärken.'
        }
      ],
      
      wholesaleTitle: 'Großhandel mit Peptiden: Ihre Chance auf Erfolg',
      wholesaleIntro: 'Wir verstehen die Dynamik des Geschäfts und die Bedürfnisse unserer Partner. Deshalb bietet PenLab Peptide spezielle Konditionen für Vertriebspartner, die unsere Produkte im Großhandel beziehen möchten:',
      wholesalePoints: [
        'Attraktive Preise mit erheblichen Rabatten bei Großbestellungen',
        'Schnelle und sichere Lieferung bei der richtigen Temperatur überall auf der Welt',
        'Spezialisierte Beratung und Schulung zu Produkten und ihrer Anwendung'
      ],
      
      whyTitle: 'Warum Vertriebspartner von uns werden?',
      whyReasons: [
        {
          title: 'Hohe Qualität',
          description: 'Unsere Priorität war stets die Produktqualität, die das Vertrauen und die Zufriedenheit der Kunden gewährleistet.'
        },
        {
          title: 'Schulung & Unterstützung',
          description: 'Wir bieten Schulungen, Ressourcen und kontinuierliche Unterstützung für alle unsere Vertriebspartner, damit sie unsere Produkte erfolgreich präsentieren und vertreiben können.'
        },
        {
          title: 'Faire Konditionen',
          description: 'Wir schätzen unsere Partner und bieten wettbewerbsfähige Rabatte und flexible Bedingungen.'
        }
      ],
      
      whiteLabelTitle: 'White-Label-Lösungen',
      whiteLabelIntro: 'Bauen Sie Ihre eigene Marke mit unserem White-Label-Programm auf:',
      whiteLabelPoints: [
        'Individuelles Branding mit Ihrem Logo und Design',
        'Professionelle Verpackung und Etikettierung',
        'Vollständige Produktdokumentation',
        'Unterstützende Marketingmaterialien'
      ],
      whiteLabelMin: 'Mindestbestellmenge',
      whiteLabelMinDesc: '1.000 Einheiten pro Produkt pro Monat',
      
      expectTitle: 'Was wir von Ihnen erwarten',
      expectations: [
        'Bereitschaft zu wachsen und zu lernen',
        'Kenntnis des Marktes und der Kunden in Ihrem Land oder Ihrer Region',
        'Engagement für die hohen Standards an Service und Professionalität, die PenLab Peptide einhält'
      ],
      
      ctaTitle: 'Bereit loszulegen?',
      ctaDesc: 'Kontaktieren Sie uns noch heute, um mehr über Partnerschaftsmöglichkeiten zu erfahren oder um den Partnerschaftsprozess zu beginnen.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Kontakt aufnehmen',
      
      slogan: 'PenLab Peptide, Gemeinsam verändern wir die Zukunft der Peptide!'
    },
    fr: {
      title: 'Devenez notre distributeur',
      subtitle: 'Peptides de gros : votre opportunité de succès',
      intro: "Bonjour, si vous êtes ici, vous avez probablement entendu parler de PenLab Peptide, un fournisseur de premier plan de peptides de haute qualité {loc} et dans le monde entier. Nous proposons une large gamme de peptides pour des applications scientifiques, médicales et cosmétiques.",
      
      featuresTitle: 'Caractéristiques de nos produits',
      features: [
        {
          title: 'Format unique',
          description: 'Nos peptides sont proposés au format « stylo à peptide », combinant praticité et efficacité pour une administration aisée et un dosage précis.'
        },
        {
          title: 'Option marque blanche',
          description: 'Pour les clients et distributeurs achetant en grande quantité, nous proposons des solutions en marque blanche, permettant une identification facile de votre marque et renforçant votre position sur le marché.'
        }
      ],
      
      wholesaleTitle: 'Commerce de peptides en gros : votre opportunité de succès',
      wholesaleIntro: 'Nous comprenons la dynamique des affaires et les besoins de nos partenaires. C\'est pourquoi PenLab Peptide propose des conditions spéciales aux distributeurs qui souhaitent acheter nos produits en gros :',
      wholesalePoints: [
        'Prix attractifs avec des remises importantes sur les grosses commandes',
        'Livraison rapide et sécurisée à la bonne température partout dans le monde',
        'Consultations spécialisées et formations sur les produits et leurs applications'
      ],
      
      whyTitle: 'Pourquoi devenir notre distributeur ?',
      whyReasons: [
        {
          title: 'Haute qualité',
          description: 'Notre priorité a toujours été la qualité des produits, ce qui garantit la confiance et la satisfaction des clients.'
        },
        {
          title: 'Formation & support',
          description: 'Nous offrons des formations, des ressources et un accompagnement constant à tous nos distributeurs afin qu’ils puissent présenter et vendre nos produits avec succès.'
        },
        {
          title: 'Conditions avantageuses',
          description: 'Nous valorisons nos partenaires et proposons des remises compétitives et des conditions flexibles.'
        }
      ],
      
      whiteLabelTitle: 'Solutions en marque blanche',
      whiteLabelIntro: 'Construisez votre propre marque avec notre programme de marque blanche :',
      whiteLabelPoints: [
        'Personnalisation avec votre logo et votre design',
        'Emballage et étiquetage professionnels',
        'Documentation produit complète',
        'Supports de marketing'
      ],
      whiteLabelMin: 'Quantité minimale de commande',
      whiteLabelMinDesc: '1 000 unités par produit et par mois',
      
      expectTitle: 'Ce que nous attendons de vous',
      expectations: [
        'Volonté de grandir et d’apprendre',
        'Connaissance du marché et des clients dans votre pays ou région',
        'Engagement envers les normes élevées de service et de professionnalisme maintenues par PenLab Peptide'
      ],
      
      ctaTitle: 'Prêt à commencer ?',
      ctaDesc: 'Contactez-nous dès aujourd’hui pour en savoir plus sur les possibilités de partenariat ou pour entamer le processus de collaboration.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Nous contacter',
      
      slogan: 'PenLab Peptide - Ensemble, nous changeons l’avenir des peptides !'
    },
    it: {
      title: 'Diventa nostro distributore',
      subtitle: 'Peptidi all’ingrosso: la tua opportunità di successo',
      intro: "Ciao, se sei qui probabilmente hai sentito parlare di PenLab Peptide, un fornitore leader di peptidi di alta qualità {loc} e in tutto il mondo. Offriamo un\'ampia gamma di peptidi per applicazioni scientifiche, mediche e cosmetiche.",
      
      featuresTitle: 'Caratteristiche dei nostri prodotti',
      features: [
        {
          title: 'Formato unico',
          description: 'I nostri peptidi sono disponibili nel formato "penna per peptidi", che combina praticità ed efficacia per una somministrazione semplice e un dosaggio preciso.'
        },
        {
          title: 'Opzione white label',
          description: 'Per clienti e distributori che acquistano grandi quantità, offriamo soluzioni white label, che consentono una facile riconoscibilità del marchio e rafforzano la tua posizione sul mercato.'
        }
      ],
      
      wholesaleTitle: 'Commercio all’ingrosso di peptidi: la tua opportunità di successo',
      wholesaleIntro: 'Comprendiamo le dinamiche del business e le esigenze dei nostri partner. Per questo PenLab Peptide offre condizioni speciali per i distributori che desiderano acquistare i nostri prodotti all’ingrosso:',
      wholesalePoints: [
        'Prezzi interessanti con sconti significativi su grandi ordini',
        'Consegna rapida e sicura alla giusta temperatura in qualsiasi parte del mondo',
        'Consulenze specializzate e formazione sui prodotti e sul loro impiego'
      ],
      
      whyTitle: 'Perché diventare nostro distributore?',
      whyReasons: [
        {
          title: 'Alta qualità',
          description: 'La nostra priorità è sempre stata la qualità del prodotto, che garantisce fiducia e soddisfazione del cliente.'
        },
        {
          title: 'Formazione e supporto',
          description: 'Offriamo formazione, risorse e supporto costante a tutti i nostri distributori, così che possano presentare e vendere con successo i nostri prodotti.'
        },
        {
          title: 'Condizioni vantaggiose',
          description: 'Valorizziamo i nostri partner e offriamo sconti competitivi e termini flessibili.'
        }
      ],
      
      whiteLabelTitle: 'Soluzioni white label',
      whiteLabelIntro: 'Costruisci il tuo brand con il nostro programma white label:',
      whiteLabelPoints: [
        'Brand personalizzato con il tuo logo e il tuo design',
        'Confezionamento ed etichettatura professionali',
        'Documentazione completa del prodotto',
        'Materiali di supporto marketing'
      ],
      whiteLabelMin: 'Ordine minimo richiesto',
      whiteLabelMinDesc: '1.000 unità per prodotto al mese',
      
      expectTitle: 'Cosa ci aspettiamo da te',
      expectations: [
        'Desiderio di crescere e imparare',
        'Conoscenza del mercato e dei clienti nel tuo paese o nella tua regione',
        'Impegno verso gli elevati standard di servizio e professionalità mantenuti da PenLab Peptide'
      ],
      
      ctaTitle: 'Pronto per iniziare?',
      ctaDesc: 'Contattaci oggi per saperne di più sulle opportunità di partnership o per iniziare il processo di collaborazione.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Contattaci',
      
      slogan: 'PenLab Peptide - Insieme cambiamo il futuro dei peptidi!'
    },
    pl: {
      title: 'Zostań naszym dystrybutorem',
      subtitle: 'Hurtowe peptydy: Twoja szansa na sukces',
      intro: 'Witaj, jeśli tu jesteś, prawdopodobnie słyszałeś o PenLab Peptide, wiodącym dostawcy wysokiej jakości peptydów {loc} i na całym świecie. Oferujemy szeroką gamę peptydów do zastosowań naukowych, medycznych i kosmetycznych.',
      
      featuresTitle: 'Cechy naszych produktów',
      features: [
        {
          title: 'Unikalny format',
          description: 'Nasze peptydy są dostępne w formie „peptide pen”, łącząc wygodę i skuteczność dla łatwej aplikacji i precyzyjnego dawkowania.'
        },
        {
          title: 'Opcja white label',
          description: 'Dla klientów i dystrybutorów kupujących w dużych ilościach oferujemy rozwiązania white label, umożliwiające łatwą rozpoznawalność marki i wzmocnienie pozycji na rynku.'
        }
      ],
      
      wholesaleTitle: 'Hurtowy handel peptydami: Twoja szansa na sukces',
      wholesaleIntro: 'Rozumiemy dynamikę biznesu i potrzeby naszych partnerów. Dlatego PenLab Peptide oferuje specjalne warunki dla dystrybutorów, którzy chcą kupować nasze produkty hurtowo:',
      wholesalePoints: [
        'Atrakcyjne ceny z znacznymi rabatami przy dużych zamówieniach',
        'Szybka i bezpieczna dostawa w odpowiedniej temperaturze w dowolne miejsce na świecie',
        'Specjalistyczne konsultacje i szkolenia na temat produktów i ich zastosowań'
      ],
      
      whyTitle: 'Dlaczego warto zostać naszym dystrybutorem?',
      whyReasons: [
        {
          title: 'Wysoka jakość',
          description: 'Naszym priorytetem zawsze była jakość produktów, która gwarantuje zaufanie i zadowolenie klientów.'
        },
        {
          title: 'Szkolenia i wsparcie',
          description: 'Oferujemy szkolenia, materiały i stałe wsparcie wszystkim naszym dystrybutorom, aby mogli skutecznie prezentować i sprzedawać nasze produkty.'
        },
        {
          title: 'Lojalne warunki',
          description: 'Cenimy naszych partnerów i oferujemy konkurencyjne rabaty oraz elastyczne warunki.'
        }
      ],
      
      whiteLabelTitle: 'Rozwiązania white label',
      whiteLabelIntro: 'Zbuduj własną markę dzięki naszemu programowi white label:',
      whiteLabelPoints: [
        'Indywidualny branding z Twoim logo i projektem',
        'Profesjonalne opakowanie i etykietowanie',
        'Pełna dokumentacja produktu',
        'Materiały wspierające marketing'
      ],
      whiteLabelMin: 'Minimalne wymagania dotyczące zamówienia',
      whiteLabelMinDesc: '1 000 sztuk na produkt miesięcznie',
      
      expectTitle: 'Czego od Ciebie oczekujemy',
      expectations: [
        'Chęci rozwoju i nauki',
        'Znajomości rynku i klientów w Twoim kraju lub regionie',
        'Zaangażowania w utrzymanie wysokich standardów obsługi i profesjonalizmu, które PenLab Peptide zachowuje'
      ],
      
      ctaTitle: 'Gotowy, aby zacząć?',
      ctaDesc: 'Skontaktuj się z nami już dziś, aby dowiedzieć się więcej o możliwościach partnerstwa lub rozpocząć proces współpracy.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Skontaktuj się z nami',
      
      slogan: 'PenLab Peptide, razem zmieniamy przyszłość peptydów!'
    },
    nl: {
      title: 'Word onze distributeur',
      subtitle: 'Groothandel in peptiden: uw kans op succes',
      intro: "Hallo, als u hier bent, heeft u waarschijnlijk gehoord van PenLab Peptide, een toonaangevende leverancier van hoogwaardige peptiden {loc} en wereldwijd. Wij bieden een breed scala aan peptiden voor wetenschappelijke, medische en cosmetische toepassingen.",
      
      featuresTitle: 'Kenmerken van onze producten',
      features: [
        {
          title: 'Uniek formaat',
          description: 'Onze peptiden worden geleverd in een "peptidepen"-formaat, dat gebruiksgemak en doeltreffendheid combineert voor eenvoudige toediening en nauwkeurige dosering.'
        },
        {
          title: 'White-labeloptie',
          description: 'Voor klanten en distributeurs die in grote hoeveelheden inkopen, bieden wij white-labeloplossingen, waarmee eenvoudige merkherkenning mogelijk wordt en uw marktpositie wordt versterkt.'
        }
      ],
      
      wholesaleTitle: 'Groothandel in peptiden: uw kans op succes',
      wholesaleIntro: 'Wij begrijpen de dynamiek van het bedrijfsleven en de behoeften van onze partners. Daarom biedt PenLab Peptide speciale voorwaarden voor distributeurs die onze producten in het groot willen inkopen:',
      wholesalePoints: [
        'Aantrekkelijke prijzen met aanzienlijke kortingen op grote bestellingen',
        'Snelle en veilige levering op de juiste temperatuur, overal ter wereld',
        'Gespecialiseerde consultaties en trainingen over de producten en hun toepassingen'
      ],
      
      whyTitle: 'Waarom onze distributeur worden?',
      whyReasons: [
        {
          title: 'Hoge kwaliteit',
          description: 'Onze prioriteit is altijd de productkwaliteit geweest, wat vertrouwen en tevredenheid bij klanten garandeert.'
        },
        {
          title: 'Training & ondersteuning',
          description: 'Wij bieden trainingen, middelen en voortdurende ondersteuning aan al onze distributeurs, zodat zij onze producten succesvol kunnen presenteren en verkopen.'
        },
        {
          title: 'Loyale voorwaarden',
          description: 'Wij waarderen onze partners en bieden concurrerende kortingen en flexibele voorwaarden.'
        }
      ],
      
      whiteLabelTitle: 'White-labeloplossingen',
      whiteLabelIntro: 'Bouw uw eigen merk op met ons white-labelprogramma:',
      whiteLabelPoints: [
        'Aangepaste branding met uw logo en ontwerp',
        'Professionele verpakking en etikettering',
        'Volledige productdocumentatie',
        'Marketing-ondersteuningsmateriaal'
      ],
      whiteLabelMin: 'Minimale bestelhoeveelheid',
      whiteLabelMinDesc: '1.000 eenheden per product per maand',
      
      expectTitle: 'Wat wij van u verwachten',
      expectations: [
        'Bereidheid om te groeien en te leren',
        'Kennis van de markt en klanten in uw land of regio',
        'Toewijding aan de hoge normen van service en professionaliteit die PenLab Peptide hanteert'
      ],
      
      ctaTitle: 'Klaar om te beginnen?',
      ctaDesc: 'Neem vandaag nog contact met ons op voor meer informatie over partnerschapsmogelijkheden of om het partnerschapsproces te starten.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Neem contact op',
      
      slogan: 'PenLab Peptide - samen veranderen we de toekomst van peptiden!'
    },
    pt: {
      title: 'Torne-se Nosso Distribuidor',
      subtitle: 'Peptídeos por Atacado: Sua Oportunidade de Sucesso',
      intro: "Olá, se você está aqui, provavelmente já ouviu falar da PenLab Peptide, um fornecedor líder de peptídeos de alta qualidade {loc} e em todo o mundo. Oferecemos uma ampla gama de peptídeos para aplicações científicas, médicas e cosméticas.",
      
      featuresTitle: 'Características dos Nossos Produtos',
      features: [
        {
          title: 'Formato Único',
          description: 'Nossos peptídeos vêm no formato de "caneta de peptídeo", combinando conveniência e eficácia para administração fácil e dosagem precisa.'
        },
        {
          title: 'Opção White Label',
          description: 'Para clientes e distribuidores que compram em grandes quantidades, oferecemos soluções white label, permitindo fácil reconhecimento da marca e fortalecendo sua posição no mercado.'
        }
      ],
      
      wholesaleTitle: 'Comércio de Peptídeos por Atacado: Sua Oportunidade de Sucesso',
      wholesaleIntro: 'Nós entendemos a dinâmica dos negócios e as necessidades de nossos parceiros. É por isso que a PenLab Peptide oferece condições especiais para distribuidores que desejam comprar nossos produtos no atacado:',
      wholesalePoints: [
        'Preços atrativos com descontos significativos em grandes pedidos',
        'Entrega rápida e segura na temperatura adequada para qualquer lugar do mundo',
        'Consultorias especializadas e treinamentos sobre os produtos e sua aplicação'
      ],
      
      whyTitle: 'Por Que se Tornar Nosso Distribuidor?',
      whyReasons: [
        {
          title: 'Alta Qualidade',
          description: 'Nossa prioridade sempre foi a qualidade do produto, o que garante a confiança e a satisfação dos clientes.'
        },
        {
          title: 'Treinamento & Suporte',
          description: 'Oferecemos treinamento, recursos e suporte constante a todos os nossos distribuidores para que possam apresentar e vender nossos produtos com sucesso.'
        },
        {
          title: 'Condições Vantajosas',
          description: 'Valorizamos nossos parceiros e oferecemos descontos competitivos e condições flexíveis.'
        }
      ],
      
      whiteLabelTitle: 'Soluções White Label',
      whiteLabelIntro: 'Construa sua própria marca com nosso programa white label:',
      whiteLabelPoints: [
        'Branding personalizado com seu logotipo e design',
        'Embalagem e rotulagem profissionais',
        'Documentação completa do produto',
        'Materiais de apoio de marketing'
      ],
      whiteLabelMin: 'Requisito de Pedido Mínimo',
      whiteLabelMinDesc: '1.000 unidades por produto por mês',
      
      expectTitle: 'O Que Esperamos de Você',
      expectations: [
        'Vontade de crescer e aprender',
        'Conhecimento do mercado e dos clientes em seu país ou região',
        'Compromisso com os altos padrões de serviço e profissionalismo que a PenLab Peptide mantém'
      ],
      
      ctaTitle: 'Pronto para Começar?',
      ctaDesc: 'Entre em contato conosco hoje para saber mais sobre oportunidades de parceria ou para iniciar o processo de parceria.',
      contactEmail: 'info@penlabpeptide.com',
      contactBtn: 'Entre em Contato',
      
      slogan: 'PenLab Peptide - Juntos mudamos o futuro dos peptídeos!'
    },
  };

  const t = translations[currentLanguage] || translations.en;

  const iconColors = [
    'from-capsule-teal to-capsule-aqua',
    'from-capsule-coral to-capsule-salmon',
    'from-purple-500 to-violet-500'
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Section - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* Decorative elements */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Building2 className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{tg('distributor_partnership')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {t.title}
            </h1>
            <p className="text-xl md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua mb-6">
              {t.subtitle}
            </p>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              {t.intro.replace('{loc}', inPhrase)}
            </p>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-16">
        {/* Product Features */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="text-capsule-teal text-sm font-semibold tracking-wider uppercase">{tg('distributor_advantages')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">{t.featuresTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {t.features.map((feature, index) => (
              <div key={index} className="glass-card rounded-3xl p-8 hover:shadow-glass-hover transition-all duration-300">
                <div className={`w-14 h-14 bg-gradient-to-br ${index === 0 ? 'from-capsule-teal to-capsule-aqua' : 'from-capsule-coral to-capsule-salmon'} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                  {index === 0 ? <Package className="w-7 h-7 text-white" /> : <Tag className="w-7 h-7 text-white" />}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Wholesale Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-8 h-8" />
                <h2 className="text-3xl font-bold">{t.wholesaleTitle}</h2>
              </div>
              <p className="text-lg opacity-90 mb-8 max-w-2xl">{t.wholesaleIntro}</p>
              <ul className="space-y-4">
                {t.wholesalePoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <span className="text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Why Become Distributor */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="text-capsule-teal text-sm font-semibold tracking-wider uppercase">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">{t.whyTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.whyReasons.map((reason, index) => {
              const icons = [Award, HeadphonesIcon, Users];
              const Icon = icons[index];
              return (
                <div key={index} className="glass-card rounded-3xl p-8 text-center hover:shadow-glass-hover transition-all duration-300">
                  <div className={`w-16 h-16 bg-gradient-to-br ${iconColors[index]} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* White Label Section */}
        <div className="mb-16">
          <div className="glass-card rounded-3xl overflow-hidden">
            <div className="bg-gray-900 text-white p-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-2xl flex items-center justify-center">
                  <Building2 className="w-7 h-7" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">{t.whiteLabelTitle}</h2>
              </div>
            </div>
            <div className="p-8">
              <p className="text-lg text-gray-700 mb-6">{t.whiteLabelIntro}</p>
              <ul className="grid md:grid-cols-2 gap-4 mb-8">
                {t.whiteLabelPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-gradient-to-r from-capsule-coral/10 to-capsule-salmon/10 border border-capsule-coral/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Package className="w-6 h-6 text-capsule-coral" />
                  <h3 className="text-xl font-bold text-gray-900">{t.whiteLabelMin}</h3>
                </div>
                <p className="text-2xl font-bold text-capsule-coral">{t.whiteLabelMinDesc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* What We Expect */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <span className="text-capsule-teal text-sm font-semibold tracking-wider uppercase">{tg('distributor_requirements')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">{t.expectTitle}</h2>
          </div>
          <div className="glass-card rounded-3xl p-8">
            <ul className="space-y-4">
              {t.expectations.map((expectation, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-lg text-gray-700 pt-1">{expectation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark rounded-3xl p-8 md:p-12 text-white text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-capsule-teal/20 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-capsule-aqua/20 rounded-full blur-3xl translate-y-1/2 translate-x-1/2"></div>
          
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Sparkles className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{tg('distributor_start_today')}</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.ctaTitle}</h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">{t.ctaDesc}</p>
            
            <a 
              href={`mailto:${t.contactEmail}`}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-teal-glow hover:shadow-lg hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              {t.contactBtn}
            </a>
            
            <p className="mt-8 text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua italic">
              {t.slogan}
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DistributorPage;
